import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Shield, ShieldCheck, Award, HardHat, CheckCircle2, Mail, MessageCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';

const LoginPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(formData.email, formData.password);

    if (result.success) {
      toast({
        title: "Login erfolgreich!",
        description: "Willkommen zurück"
      });
      navigate('/dashboard');
    } else {
      toast({
        title: "Login fehlgeschlagen",
        description: result.error || "Bitte überprüfen Sie Ihre Zugangsdaten",
        variant: "destructive"
      });
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-950 to-gray-900 text-white overflow-hidden">
      <div className="min-h-screen grid grid-cols-1 lg:grid-cols-5">

        {/* LINKS: Trust-Panel mit BG-konform-Badge + Ansprechpartner */}
        <div className="hidden lg:flex lg:col-span-2 relative overflow-hidden bg-gradient-to-br from-yellow-500/[0.07] via-gray-950 to-amber-500/[0.05] border-r border-yellow-500/20">
          {/* Deko-Glows */}
          <div className="absolute -top-40 -left-20 w-96 h-96 bg-yellow-500/15 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute bottom-0 right-0 w-80 h-80 bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />

          <div className="relative z-10 flex flex-col justify-between p-10 xl:p-14 w-full">
            {/* Logo */}
            <div className="cursor-pointer inline-flex" onClick={() => navigate('/')} data-testid="login-logo">
              <img src="/assets/smartvolt-logo.png" alt="SmartVolt Academy" className="h-16 xl:h-20 w-auto drop-shadow-lg" />
            </div>

            {/* Zentrales Trust-Badge */}
            <div className="my-8">
              <div
                className="relative rounded-2xl border-2 border-yellow-500/50 bg-gradient-to-br from-yellow-500/15 to-amber-500/5 p-6 xl:p-8 shadow-2xl shadow-yellow-500/10"
                data-testid="login-trust-badge"
              >
                <div className="absolute -top-4 -right-4 bg-yellow-500 text-black rounded-full w-14 h-14 flex items-center justify-center shadow-xl border-4 border-gray-950">
                  <ShieldCheck className="w-7 h-7" strokeWidth={2.5} />
                </div>

                <div className="flex items-center gap-2 text-yellow-400 text-xs font-black uppercase tracking-widest mb-3">
                  <Award className="w-4 h-4" />
                  <span>Rechtssichere Weiterbildung</span>
                </div>

                <h2 className="text-white text-2xl xl:text-3xl font-black leading-tight mb-4">
                  BG-konform<br/>&amp; <span className="text-yellow-400">rechtssicher</span>
                </h2>

                <ul className="space-y-2.5 text-sm">
                  <li className="flex items-start gap-2.5">
                    <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 shrink-0" />
                    <span className="text-gray-200">Nach <strong>BetrSichV</strong>, <strong>DGUV V1 &amp; V3</strong></span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 shrink-0" />
                    <span className="text-gray-200">Automatische, personalisierte Zertifikate</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 shrink-0" />
                    <span className="text-gray-200">BG-Prüfungs-Nachweis als ZIP-Export</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 shrink-0" />
                    <span className="text-gray-200">Entwickelt von einem <strong>Elektromeister</strong></span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Ansprechpartner-Hinweis */}
            <div className="rounded-xl border border-gray-800 bg-black/40 backdrop-blur-sm p-5" data-testid="login-support-box">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-full overflow-hidden shrink-0 border-2 border-yellow-500/60 shadow-lg shadow-yellow-500/20">
                  <img
                    src="/assets/martin-seidel.webp"
                    alt="Martin Seidel"
                    className="w-full h-full object-cover"
                    style={{ objectPosition: 'center 25%' }}
                    loading="eager"
                  />
                </div>
                <div className="min-w-0">
                  <p className="text-yellow-400 text-xs font-bold uppercase tracking-wider mb-1">Direkter Draht zum Chef</p>
                  <p className="text-white text-sm font-bold mb-1">Martin Seidel</p>
                  <p className="text-gray-400 text-xs mb-3">Gründer &amp; Elektromeister</p>
                  <p className="text-gray-300 text-xs italic leading-relaxed mb-3">
                    „Bei Problemen antworte ich dir persönlich – meist innerhalb von 24 Stunden."
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <a
                      href="mailto:smartvolt-academy@gmx.net"
                      className="inline-flex items-center gap-1.5 text-xs text-yellow-300 hover:text-yellow-200 border border-yellow-500/30 hover:border-yellow-500/60 rounded-full px-3 py-1.5 transition"
                      data-testid="login-support-email"
                    >
                      <Mail className="w-3.5 h-3.5" />
                      smartvolt-academy@gmx.net
                    </a>
                    <button
                      onClick={() => navigate('/impressum')}
                      className="inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-yellow-300 border border-gray-700 hover:border-yellow-500/40 rounded-full px-3 py-1.5 transition"
                      data-testid="login-impressum-link"
                    >
                      <MessageCircle className="w-3.5 h-3.5" />
                      Impressum &amp; Kontakt
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* RECHTS: Login-Formular */}
        <div className="col-span-1 lg:col-span-3 flex items-center justify-center p-6 sm:p-10">
          <div className="w-full max-w-md">
            {/* Mobile-Logo (nur < lg) */}
            <div className="flex items-center justify-center gap-2 mb-8 cursor-pointer lg:hidden" onClick={() => navigate('/')}>
              <img src="/assets/smartvolt-logo.png" alt="SmartVolt Academy" className="h-16" />
            </div>

            {/* Mobile-Badge (nur < lg, dezent) */}
            <div className="flex items-center justify-center mb-6 lg:hidden">
              <span
                className="inline-flex items-center gap-1.5 bg-yellow-500/10 border border-yellow-500/40 text-yellow-300 px-3 py-1.5 rounded-full text-xs font-bold"
                data-testid="login-mobile-badge"
              >
                <Shield className="w-3.5 h-3.5" /> BG-konform &amp; rechtssicher
              </span>
            </div>

            <Card className="bg-gradient-to-br from-gray-900 to-black border-yellow-600/20 shadow-2xl">
              <CardHeader className="text-center">
                <CardTitle className="text-2xl text-white">Anmelden</CardTitle>
                <CardDescription className="text-gray-400">Melden Sie sich in Ihrem Firmenkonto an</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="email" className="text-gray-300">E-Mail</Label>
                    <Input
                      id="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="bg-gray-800 border-yellow-600/20 text-white focus:border-yellow-500"
                      placeholder="info@firma.de"
                      data-testid="login-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="password" className="text-gray-300">Passwort</Label>
                    <Input
                      id="password"
                      type="password"
                      required
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="bg-gray-800 border-yellow-600/20 text-white focus:border-yellow-500"
                      placeholder="Ihr Passwort"
                      data-testid="login-password-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-yellow-600 to-yellow-500 text-black font-semibold hover:from-yellow-500 hover:to-yellow-400"
                    data-testid="login-submit-btn"
                  >
                    {loading ? 'Anmeldung läuft...' : 'Anmelden'}
                  </Button>
                </form>
                <div className="mt-4 text-center text-sm text-gray-400">
                  Noch kein Konto?{' '}
                  <span className="text-yellow-500 cursor-pointer hover:underline" onClick={() => navigate('/register')} data-testid="login-register-link">
                    Jetzt registrieren
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Mobile-Kontakt (dezent unten) */}
            <div className="mt-6 text-center lg:hidden">
              <p className="text-gray-500 text-xs">
                Fragen? Der Chef antwortet persönlich —{' '}
                <a href="mailto:smartvolt-academy@gmx.net" className="text-yellow-400 hover:underline">
                  smartvolt-academy@gmx.net
                </a>
              </p>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default LoginPage;
